<template>
  <base-stack class="payment" v-if="meetAndGreetTicketPayment">
    <base-stack as="header" direction="column">
      <base-stack direction="row" justify="space-between" align="center">
        <base-stack direction="row" align="center" :spacing="4">
          <h2>{{ meetAndGreetTicketPayment.email }}</h2>
        </base-stack>
        <base-stack direction="row">
          <base-button :to="{ name: 'users' }">← Back to Users</base-button>
        </base-stack>
      </base-stack>
    </base-stack>
    <base-stack direction="column" :spacing="8">
      <h3>Secret Gathering public purchase {{ meetAndGreetTicketPayment.refundedAt ? '(refunded!)' : '' }}</h3>
      <base-stack as="dl">
        <div>
          <dt>Name</dt>
          <dd>{{ meetAndGreetTicketPayment.name }} (<a :href="stripeUrl" target="_blank">Stripe</a>)</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{{ meetAndGreetTicketPayment.phone }}</dd>
        </div>
        <div>
          <dt>Show</dt>
          <dd>
            <router-link
              :to="{
                name: 'shows',
                params: { tourId: meetAndGreetTicketPayment.ticket.show.tourId },
                hash: '#' + meetAndGreetTicketPayment.ticket.show.id,
              }"
              >{{ meetAndGreetTicketPayment.ticket.show.address.venue }},
              {{ meetAndGreetTicketPayment.ticket.show.showDate | dayjs.utc('MMM DD, YYYY') }}</router-link
            >
          </dd>
        </div>
        <div>
          <dt>Purchase amount</dt>
          <dd>{{ meetAndGreetTicketPayment.amountInCents | usd.cents }}</dd>
        </div>
        <div>
          <dt>Purchased at</dt>
          <dd>
            {{ meetAndGreetTicketPayment.createdAt | dayjs('MMM DD, YYYY (HH:mm)') }}
            &nbsp;(<a :href="meetAndGreetTicketPayment.receiptUrl" target="_blank">Receipt</a>)
          </dd>
        </div>
        <div v-if="meetAndGreetTicketPayment.refundedAt">
          <dt>Refunded at</dt>
          <dd>{{ meetAndGreetTicketPayment.refundedAt | dayjs('MMM DD, YYYY (HH:mm)') }}</dd>
        </div>
        <div>
          <dt>Valid for</dt>
          <dd>{{ meetAndGreetTicketPayment.ticket.isValidFor }}</dd>
        </div>
        <div>
          <dt>Items</dt>
          <dd>
            <base-stack as="ul" :spacing="2">
              <li v-for="item in meetAndGreetTicketPayment.ticket.package?.items" :key="item.itemId">
                {{ item.title }} x {{ item.quantity }}
              </li>
            </base-stack>
          </dd>
        </div>
      </base-stack>
      <base-stack direction="row">
        <base-button @click="onRefund" :disabled="meetAndGreetTicketPayment.refundedAt">Refund</base-button>
      </base-stack>
    </base-stack>
  </base-stack>
</template>

<script lang="ts">
import { MeetAndGreetTicketPayment } from '@/types/api';
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({
  apollo: {
    meetAndGreetTicketPayment: {
      query: gql`
        query MeetAndGreetTicketPayment($id: ID) {
          meetAndGreetTicketPayment(id: $id) {
            id
            name
            phone
            email
            amountInCents
            currency
            receiptUrl
            refundedAt
            createdAt
            stripeId
            ticket {
              id
              show {
                id
                showDate
                tourId
                address {
                  venue
                }
              }
              isValidFor
              package(includeGifted: true) {
                items {
                  itemId
                  title
                  imageUrls
                  quantity
                }
              }
            }
          }
        }
      `,
      variables() {
        return { id: this.id };
      },
      fetchPolicy: 'network-only',
    },
  },
})
export default class MeetAndGreetTicketPaymentView extends Vue {
  @Prop({ type: String, required: true }) public id!: string;

  meetAndGreetTicketPayment: MeetAndGreetTicketPayment | null = null;

  get stripeUrl(): string | null {
    if (!this.meetAndGreetTicketPayment?.stripeId) {
      return null;
    }

    return `https://dashboard.stripe.com/customers/${this.meetAndGreetTicketPayment.stripeId}`;
  }

  async onRefund() {
    const confirm = await this.$modal.confirm({
      title: 'Refund ticket payment',
      message: 'Are you sure you want to refund this ticket payment?',
      confirmButtonText: 'Refund',
    });
    if (!confirm) return;
    await this.$apollo.mutate({
      mutation: gql`
        mutation RefundMeetAndGreetTicketPayment($input: RefundMeetAndGreetTicketPaymentInput!) {
          refundMeetAndGreetTicketPayment(input: $input) {
            payment {
              id
              refundedAt
            }
          }
        }
      `,
      variables: { input: { id: this.id } },
      refetchQueries: ['MeetAndGreetTicketPayment'],
      awaitRefetchQueries: true,
    });
  }
}
</script>

<style lang="scss" scoped>
.payment {
  h2 {
    color: $color-theme-red;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
  }

  header {
    padding: 1rem 0;
    border-bottom: 1px solid $color-theme-yellow;
  }

  h3 {
    padding-top: 1rem;
    color: $color-theme-red;
  }

  dl {
    max-width: 600px;
  }

  dl > div {
    display: flex;
    justify-content: space-between;
  }
}
</style>
